import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Download, FileSearch, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useSelectableAgents } from "@/hooks/agent";
import { useConversationAnalysis } from "@/hooks/conversation/useConversationAnalysis";
import { useConversationDigest } from "@/hooks/analytics/useAnalytics";
import { useInsightsActionsSlot } from "@/pages/UserHub/InsightsHub/context";
import { ConversationAnalysisService } from "@/services/conversation/ConversationAnalysisService";
import { useBrain } from "@/contexts/ui/brain";
import { ConversationDetails } from "@/types/conversation/conversation";
import { formatDate, formatDuration } from "@/utils/formatting/general";
import { AnalysisFilters } from "./components/AnalysisFilters";
import { ConversationAnalysisList } from "./components/ConversationAnalysisList";
import { InsightsDigest } from "./components/InsightsDigest";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

type InsightsView = "insights" | "conversations";

const MASCOT_SRC = "/genie-mascots/role/support.png";

const DATE_RANGE_LABELS: Record<string, string> = {
  "7d": "the last 7 days",
  "30d": "the last 30 days",
  "90d": "the last 90 days",
  all: "all time",
};

const ANALYSIS_FILTER_LABELS: Record<string, string> = {
  all: "",
  analyzed: " (only conversations with analysis)",
  unanalyzed: " (only conversations without analysis yet)",
};

const SUMMARY_CHAR_LIMIT = 600;

const truncate = (text: string, limit: number) =>
  text.length > limit ? `${text.slice(0, limit).trim()}…` : text;

const formatLeadInfo = (leadInfo: Record<string, unknown> | null | undefined) => {
  if (!leadInfo) return "";
  const entries = Object.entries(leadInfo).filter(
    ([, value]) => value !== null && value !== undefined && value !== "",
  );
  if (entries.length === 0) return "";
  return entries
    .map(([key, value]) => `${key}: ${String(value)}`)
    .join("; ");
};

const formatConversationForPrompt = (conversation: ConversationDetails, index: number) => {
  const date = conversation.conversation_date
    ? formatDate(conversation.conversation_date)
    : "Unknown date";
  const duration = conversation.duration_seconds
    ? formatDuration(conversation.duration_seconds)
    : "Unknown duration";
  const summary =
    conversation.summary ||
    conversation.analysis_result?.summary ||
    conversation.analysis_result?.brief_summary ||
    "(no summary available)";
  const topics =
    conversation.topics?.length
      ? conversation.topics.join(", ")
      : conversation.analysis_result?.topics?.join(", ") ||
        conversation.analysis_result?.main_topics?.join(", ") ||
        "—";
  const leadInfoText = formatLeadInfo(
    conversation.lead_info || conversation.analysis_result?.lead_info,
  );

  const lines = [
    `**${index + 1}. ${conversation.genie_name ?? "Genie"}** — ${date} · ${duration}`,
    `Summary: ${truncate(String(summary), SUMMARY_CHAR_LIMIT)}`,
    `Topics: ${topics}`,
  ];
  if (leadInfoText) lines.push(`Lead info: ${leadInfoText}`);
  return lines.join("\n");
};

const buildAskGeniePrompt = (params: {
  selectedConversations: ConversationDetails[];
  genieName: string | null;
  dateRange: string;
  analysisFilter: string;
}) => {
  const { selectedConversations, genieName, dateRange, analysisFilter } = params;
  const genieScope = genieName ? `for **${genieName}**` : "across all my genies";

  if (selectedConversations.length > 0) {
    const blocks = selectedConversations
      .map((c, i) => formatConversationForPrompt(c, i))
      .join("\n\n");
    return [
      `Please analyse these ${selectedConversations.length} conversation${
        selectedConversations.length === 1 ? "" : "s"
      } ${genieScope}. The data is below — no need to look anything up.`,
      "",
      blocks,
      "",
      "Based on the above, tell me:",
      "1. The shared themes, customer questions, or pain points across these calls",
      "2. Where my genie did well — and where it could improve",
      "3. Any leads, follow-ups, or action items I shouldn't miss",
      "4. One concrete suggestion to make these conversations go better next time",
    ].join("\n");
  }

  const rangeLabel = DATE_RANGE_LABELS[dateRange] ?? "the last 30 days";
  const filterLabel = ANALYSIS_FILTER_LABELS[analysisFilter] ?? "";

  return [
    `Please analyse my conversations from ${rangeLabel} ${genieScope}${filterLabel}.`,
    "",
    "Pull the data, then walk me through:",
    "1. The top topics and customer questions",
    "2. How my genie is performing — wins, weak spots, recurring issues",
    "3. Lead quality and any follow-ups I should action",
    "4. One concrete recommendation to improve outcomes",
  ].join("\n");
};

export default function InsightsPage({
  embedded = false,
  view = "insights",
}: { embedded?: boolean; view?: InsightsView } = {}) {
  const queryClient = useQueryClient();
  const { openBrain } = useBrain();
  const { setActions: setSlotActions } = useInsightsActionsSlot();
  const { data: agents, isLoading: agentsLoading } = useSelectableAgents();
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
  const [analysisFilter, setAnalysisFilter] = useState<"all" | "analyzed" | "unanalyzed">("all");
  const [dateRange, setDateRange] = useState<string>("30d");
  const [showExportHint, setShowExportHint] = useState(false);
  const [selectedConversationIds, setSelectedConversationIds] = useState<Set<string>>(new Set());
  const pageSize = 20;

  const hasAgents = !agentsLoading && (agents?.length || 0) > 0;
  const isConversationsView = view === "conversations";

  const { conversations, totalCount, hasNextPage, fetchNextPage, isFetchingNextPage, isLoading } =
    useConversationAnalysis({
      pageSize,
      selectedAgent,
      analysisFilter,
      dateRange,
      enabled: hasAgents,
      agents: agents || [],
    });

  // AI-synthesized "story this period" — only for the insights view.
  const { data: digest, isLoading: digestLoading } = useConversationDigest(
    { agentId: selectedAgent, dateRange },
    { enabled: hasAgents && !isConversationsView },
  );

  const invalidateConversationQueries = () => {
    queryClient.invalidateQueries({
      predicate: (query) =>
        query.queryKey[0] === "conversation-analysis-infinite" ||
        query.queryKey[0] === "conversation-stats",
    });
  };

  const handleFilterChange = (filterType: "agent" | "analysis" | "date", value: string | null) => {
    if (filterType === "agent") {
      setSelectedAgent(value);
    } else if (filterType === "analysis") {
      setAnalysisFilter((value ?? "all") as "all" | "analyzed" | "unanalyzed");
    } else if (filterType === "date") {
      setDateRange(value ?? "30d");
    }
  };

  const selectedGenieName = useMemo(
    () => agents?.find((a) => a.id === selectedAgent)?.name ?? null,
    [agents, selectedAgent],
  );

  const toggleConversationSelected = useCallback((id: string) => {
    setSelectedConversationIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const visibleConversationIds = useMemo(
    () =>
      conversations
        .map((c) => c.elevenlabs_conversation_id)
        .filter((id): id is string => !!id),
    [conversations],
  );

  const allVisibleSelected =
    visibleConversationIds.length > 0 &&
    visibleConversationIds.every((id) => selectedConversationIds.has(id));

  const toggleSelectAllVisible = useCallback(() => {
    setSelectedConversationIds((prev) => {
      const next = new Set(prev);
      if (visibleConversationIds.every((id) => next.has(id))) {
        visibleConversationIds.forEach((id) => next.delete(id));
      } else {
        visibleConversationIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, [visibleConversationIds]);

  const clearSelection = useCallback(() => setSelectedConversationIds(new Set()), []);

  const handleAskGenie = useCallback(
    (mode: "filters" | "selection") => {
      const selectedConversations =
        mode === "selection"
          ? conversations.filter(
              (c) =>
                !!c.elevenlabs_conversation_id &&
                selectedConversationIds.has(c.elevenlabs_conversation_id),
            )
          : [];
      const prompt = buildAskGeniePrompt({
        selectedConversations,
        genieName: selectedGenieName,
        dateRange,
        analysisFilter,
      });
      openBrain(prompt);
      if (mode === "selection") clearSelection();
    },
    [
      conversations,
      selectedConversationIds,
      selectedGenieName,
      dateRange,
      analysisFilter,
      openBrain,
      clearSelection,
    ],
  );

  const handleAskAboutTopic = useCallback(
    (topic: string) => {
      const scope = selectedGenieName
        ? `for ${selectedGenieName}`
        : "across all my genies";
      const rangeLabel = DATE_RANGE_LABELS[dateRange] ?? "the last 30 days";
      openBrain(
        [
          `I want to dig into the topic "${topic}" ${scope} over ${rangeLabel}.`,
          "",
          "Specifically:",
          `1. How many calls mentioned ${topic} and what was the customer typically trying to do?`,
          "2. How did my genie respond — what worked, what didn't?",
          "3. Any recurring questions or pain points within this topic?",
          "4. One concrete thing I can add to the knowledge base or system prompt to handle this better.",
        ].join("\n"),
      );
    },
    [openBrain, selectedGenieName, dateRange],
  );

  const handleAskAboutSignal = useCallback(
    (signal: string) => {
      const scope = selectedGenieName
        ? `for ${selectedGenieName}`
        : "across all my genies";
      openBrain(
        [
          `This pattern keeps showing up across recent conversations ${scope}:`,
          "",
          `> ${signal}`,
          "",
          "Walk me through:",
          "1. What's actually driving this pattern?",
          "2. Which specific calls is it most visible in?",
          "3. What should I change about my genie or my process to act on it?",
        ].join("\n"),
      );
    },
    [openBrain, selectedGenieName],
  );

  const handleSmartPrompt = useCallback(
    (prompt: string) => {
      openBrain(prompt);
    },
    [openBrain],
  );

  const handleExportSelection = useCallback(() => {
    const selectedConversations = conversations.filter(
      (c) =>
        !!c.elevenlabs_conversation_id &&
        selectedConversationIds.has(c.elevenlabs_conversation_id),
    );

    if (!selectedConversations.length) {
      toast.error("No conversations selected");
      return;
    }

    try {
      const uniqueGenies = new Set(
        selectedConversations.map((c) => c.genie_name).filter(Boolean),
      );
      const fileLabel =
        uniqueGenies.size === 1
          ? (selectedConversations[0].genie_name as string)
          : "selected-conversations";
      const { count } = ConversationAnalysisService.exportConversationsToExcel(
        selectedConversations,
        fileLabel,
      );
      toast.success(
        `Exported ${count} conversation${count === 1 ? "" : "s"}`,
      );
    } catch (error) {
      console.error("Error exporting selection:", error);
      toast.error("Failed to export selection");
    }
  }, [conversations, selectedConversationIds]);

  const handleExportAnalysis = async () => {
    if (!selectedAgent) {
      toast.error("Please select a genie first");
      return;
    }

    try {
      const { genieName, count } = await ConversationAnalysisService.exportAnalysisToExcel(
        selectedAgent,
        {
          dateRange,
          analysisFilter,
        },
      );
      toast.success(`Exported ${count} conversations for ${genieName}`);
    } catch (error) {
      console.error("Error exporting analysis:", error);
      toast.error("Failed to export analysis");
    }
  };

  const actionsRow = (
    <div className="flex flex-col gap-2 w-full sm:w-auto sm:items-end">
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
        <button
          type="button"
          onClick={() => handleAskGenie("filters")}
          className="p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-md shadow-genie-primary/20 hover:shadow-lg hover:shadow-genie-primary/30 hover:scale-[1.02] transition-all"
        >
          <span className="flex items-center justify-center gap-2 px-4 py-[7px] rounded-[10px] bg-white text-sm sm:text-base font-semibold">
            <img src={MASCOT_SRC} alt="" className="h-6 w-6 object-contain" />
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
              <span className="hidden sm:inline">Ask Your Genie</span>
              <span className="sm:hidden">Ask Genie</span>
            </span>
          </span>
        </button>
        <div className="relative">
          <div
            onMouseEnter={() => !selectedAgent && setShowExportHint(true)}
            onMouseLeave={() => setShowExportHint(false)}
            onClick={() => !selectedAgent && setShowExportHint(true)}
            className="inline-block"
          >
            <Button
              variant="outline"
              onClick={selectedAgent ? handleExportAnalysis : undefined}
              disabled={!selectedAgent}
              className="text-sm sm:text-base"
            >
              <Download className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Export Analysis</span>
              <span className="sm:hidden">Export</span>
            </Button>
          </div>
          {!selectedAgent && showExportHint && (
            <span className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 text-xs text-gray-500 whitespace-nowrap bg-white px-2 py-1 rounded border shadow-sm">
              Filter by a Genie first
            </span>
          )}
        </div>
      </div>
      <p className="hidden sm:block text-xs text-[#64748B] text-right">
        Get a Smart breakdown of your conversations in chat.
      </p>
    </div>
  );

  const slotActions = (
    <div className="flex flex-row gap-2 sm:gap-3 items-center">
      <button
        type="button"
        onClick={() => handleAskGenie("filters")}
        className="p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-md shadow-genie-primary/20 hover:shadow-lg hover:shadow-genie-primary/30 hover:scale-[1.02] transition-all"
      >
        <span className="flex items-center justify-center gap-2 px-3.5 py-[7px] rounded-[10px] bg-white text-sm font-semibold">
          <img src={MASCOT_SRC} alt="" className="h-5 w-5 object-contain" />
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
            <span className="hidden sm:inline">Ask Your Genie</span>
            <span className="sm:hidden">Ask Genie</span>
          </span>
        </span>
      </button>
      <div className="relative">
        <div
          onMouseEnter={() => !selectedAgent && setShowExportHint(true)}
          onMouseLeave={() => setShowExportHint(false)}
          onClick={() => !selectedAgent && setShowExportHint(true)}
          className="inline-block"
        >
          <Button
            variant="outline"
            onClick={selectedAgent ? handleExportAnalysis : undefined}
            disabled={!selectedAgent}
            size="sm"
            className="text-sm"
          >
            <Download className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Export Analysis</span>
            <span className="sm:hidden">Export</span>
          </Button>
        </div>
        {!selectedAgent && showExportHint && (
          <span className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 text-xs text-gray-500 whitespace-nowrap bg-white px-2 py-1 rounded border shadow-sm z-10">
            Filter by a Genie first
          </span>
        )}
      </div>
    </div>
  );

  useEffect(() => {
    // Bulk Ask/Export act on the conversations table — only surface them on that tab.
    if (!embedded || !isConversationsView) return;
    setSlotActions(slotActions);
    return () => setSlotActions(null);
    // slotActions is recreated each render but only meaningfully changes when these
    // primitive state values do — depend on the underlying state to avoid a render loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded, isConversationsView, selectedAgent, showExportHint]);

  const body = (
    <>
      {agentsLoading ? (
          <div className="text-center py-16">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-4"></div>
            <p className="text-gray-600">Loading your genies...</p>
          </div>
        ) : (agents || []).length === 0 ? (
          <div className="text-center py-16">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">No Genies Found</h2>
            <p className="text-gray-600 mb-6">
              You need to create a genie first to analyze conversations.
            </p>
            <a
              href="/user-hub/genie-setup"
              className="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
            >
              Create Your First Genie
            </a>
          </div>
        ) : (
          <>
            {/* Filters - Show immediately when agents are loaded */}
            <AnalysisFilters
              agents={agents || []}
              selectedAgent={selectedAgent}
              onSelectedAgentChange={(agentId) => handleFilterChange("agent", agentId)}
              analysisFilter={analysisFilter}
              onAnalysisFilterChange={(filter) => handleFilterChange("analysis", filter)}
              dateRange={dateRange}
              onDateRangeChange={(range) => handleFilterChange("date", range)}
            />

            {isConversationsView ? (
              <>
                {/* Conversation Analysis List */}
                <ConversationAnalysisList
                  conversations={conversations}
                  isLoading={isLoading}
                  agents={agents || []}
                  invalidateConversationQueries={invalidateConversationQueries}
                  selectedIds={selectedConversationIds}
                  onToggleSelect={toggleConversationSelected}
                  onToggleSelectAllVisible={toggleSelectAllVisible}
                  allVisibleSelected={allVisibleSelected}
                />

                {/* Infinite scroll loading indicator */}
                {hasNextPage && (
                  <div
                    ref={(el) => {
                      if (el && !isLoading && !isFetchingNextPage) {
                        const observer = new IntersectionObserver(
                          (entries) => {
                            if (entries[0].isIntersecting) {
                              fetchNextPage();
                            }
                          },
                          { threshold: 0.1 },
                        );
                        observer.observe(el);
                      }
                    }}
                    className="flex justify-center py-8"
                  >
                    {isFetchingNextPage && (
                      <div className="flex items-center gap-2 text-gray-500">
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <span>Loading more conversations...</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Show total count */}
                {conversations.length > 0 && (
                  <div className="text-center py-4 text-sm text-gray-500">
                    Showing {conversations.length} of {totalCount} conversations
                  </div>
                )}

                {/* Show helper text if no conversations found and not loading */}
                {!isLoading && conversations.length === 0 && (
                  <Card className="p-12">
                    <div className="text-center">
                      <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
                        <FileSearch className="h-8 w-8 text-blue-500" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">No Conversations Found</h3>
                      <p className="text-gray-600 mb-6 max-w-md mx-auto">
                        {selectedAgent
                          ? "No conversations found for the selected genie. Try selecting a different genie or widening the date range."
                          : "No conversations found yet. Once people talk to your Genies, their calls show up here."}
                      </p>
                      {(agents || []).length > 0 && !selectedAgent && (
                        <>
                          <p className="text-gray-600 mb-4">Select a genie to filter conversations:</p>
                          <div className="flex flex-wrap gap-2 justify-center">
                            {(agents || []).slice(0, 5).map((agent) => (
                              <button
                                key={agent.id}
                                onClick={() => setSelectedAgent(agent.id)}
                                className="px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg border border-blue-200 transition-colors"
                              >
                                {agent.name}
                              </button>
                            ))}
                            {(agents || []).length > 5 && (
                              <span className="px-4 py-2 text-gray-500">+{(agents || []).length - 5} more</span>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </Card>
                )}
              </>
            ) : (
              <InsightsDigest
                conversations={conversations}
                isLoading={isLoading}
                dateRange={dateRange}
                selectedGenieName={selectedGenieName}
                onAskTopic={handleAskAboutTopic}
                onAskSignal={handleAskAboutSignal}
                onSmartPrompt={handleSmartPrompt}
                digest={digest}
                digestLoading={digestLoading}
              />
            )}
          </>
        )}
    </>
  );

  const floatingSelectionBar = isConversationsView && selectedConversationIds.size > 0 && (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 px-4 w-full max-w-[640px] pointer-events-none">
      <div className="p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-2xl shadow-genie-primary/30 pointer-events-auto">
        <div className="rounded-[14px] bg-white px-4 sm:px-5 py-3 flex items-center gap-3">
          <img
            src={MASCOT_SRC}
            alt=""
            className="h-9 w-9 rounded-lg object-cover flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-[#222B45] truncate">
              {selectedConversationIds.size} conversation
              {selectedConversationIds.size === 1 ? "" : "s"} selected
            </p>
            <p className="text-xs text-[#64748B] truncate">
              Ask Your Genie to compare, summarise, or pull insights
            </p>
          </div>
          <button
            type="button"
            onClick={handleExportSelection}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold text-gray-700 border border-gray-200 bg-white hover:bg-gray-50 transition-colors flex-shrink-0"
          >
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">Export</span>
          </button>
          <button
            type="button"
            onClick={() => handleAskGenie("selection")}
            className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-xl text-white text-sm font-semibold shadow-md shadow-genie-primary/30 hover:opacity-90 transition-opacity flex-shrink-0"
            style={{ background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }}
          >
            <span className="hidden sm:inline">Ask Your Genie</span>
            <span className="sm:hidden">Ask</span>
          </button>
          <button
            type="button"
            onClick={clearSelection}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex-shrink-0"
            aria-label="Clear selection"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );

  if (embedded) {
    return (
      <>
        {body}
        {floatingSelectionBar}
      </>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFC]">
      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8 max-w-[1920px] w-full">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 sm:mb-8 gap-4 sm:gap-0">
          <div className="flex items-center">
            <GenieIcon
              name="globe"
              size={28}
              label="Insights"
              className="h-7 w-7 md:h-8 md:w-8 mr-3 md:mr-4"
            />
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-genie-text-primary truncate">
                {isConversationsView ? "Per-call Analysis" : "Conversational Insights"}
              </h1>
              <p className="text-sm sm:text-base text-genie-text-sub mt-1 line-clamp-2">
                {isConversationsView
                  ? "Topics, lead details, and the full analysis for every individual call"
                  : "The story across your conversations — what's working, what to fix, and what to chase"}
              </p>
            </div>
          </div>
          {isConversationsView && actionsRow}
        </div>

        {body}
      </div>
      {floatingSelectionBar}
    </div>
  );
}
